import React from "react";
import { motion } from "framer-motion";
import { isEmpty } from "lodash";

type TileMode = "default" | "correct" | "incorrect" | "close";

const backgroundColors: Record<TileMode | "default", string> = {
    default: "#FFFFFF",
    correct: "#AD6BFF",
    incorrect: "#D9D9D9",
    close: "#FAB85F",
};

const animationVariants = {
    idle: {
        rotateX: 0, // Start with no vertical rotation
    },
    flip: ({ delay, backgroundColor }: { delay: number; backgroundColor: string }) => ({
        rotateX: 360, // Rotate vertically halfway to create the "flip" effect
        backgroundColor,
        transition: {
            duration: 0.5,
            ease: [0.4, 0.0, 0.2, 1], // Wordle-like easing
            delay, // Apply the delay dynamically
        },
    }),
    strobe: {
        scale: [1, 1.15, 1],
        transition: "spring" as const,
    },
};

interface TileProps {
    mode?: TileMode;
    value?: string;
    onClick?: () => void;
    animationDelay?: number;
    onAnimationComplete?: (animationName: string) => void;
}

const Tile: React.FC<TileProps> = ({ mode = "default", value, onClick, animationDelay = 0, onAnimationComplete }) => {
    const animateFlip = mode !== "default" ? "flip" : !isEmpty(value) ? "strobe" : "idle";
    const customProperties = { delay: animationDelay, backgroundColor: backgroundColors[mode] };

    return (
        <motion.div
            initial="idle"
            className="tw-flex tw-h-[30px] tw-w-[30px] tw-justify-center tw-items-center tw-text-2xl tw-font-medium tw-rounded tw-border tw-border-gray-400"
            variants={animationVariants as any}
            custom={customProperties}
            animate={animateFlip as any}
            onClick={onClick}
            onAnimationComplete={onAnimationComplete}
        >
            {value ?? ""}
        </motion.div>
    );
};

export default Tile;
